import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import store from '@/store';
import { StopwatchViewModel } from '@/services/api/nswag/stapiclient_generated';
import { stStopwatchApi } from '@/services/st-api';
import { DateTime } from 'luxon';
import { notifyError } from '@/utlitities/notifications';

@Module({ dynamic: true, store, name: 'StopwatchModule', namespaced: true })
export default class StopwatchModule extends VuexModule {
  public StopwatchModel: StopwatchViewModel = new StopwatchViewModel();
  public IsValid: boolean = false;
  public Timer: any;

  public isLoaded = false;
  public now: DateTime = DateTime.utc();
  serverDiffSeconds: number = 0;

  get IsRunning() {
    return !!this.StopwatchModel?.id;
  }

  get RunningSeconds() {
    let start = DateTime.fromISO(this.StopwatchModel.stopwatchStartDateTimeUtc as string, { zone: 'utc' });
    let diff = this.now.diff(start, 'seconds');
    if (diff.seconds <= 0) {
      return 0;
    }

    return Math.floor(diff.seconds);
  }

  @Mutation
  setIsLoaded(data: boolean) {
    this.isLoaded = data;
  }

  @Mutation
  setIsValid(data: boolean) {
    this.IsValid = data;
  }

  @Mutation
  public setStopwatchModel(data: StopwatchViewModel) {
    this.StopwatchModel = data;

    if (this.Timer) {
      clearInterval(this.Timer);
    }

    if (!!data?.id) {
      this.now = DateTime.utc().plus({ seconds: this.serverDiffSeconds });
      this.Timer = setInterval(() => {
        this.now = DateTime.utc().plus({ seconds: this.serverDiffSeconds });
      }, 1000);
    }
  }

  @Mutation
  public reset() {
    this.StopwatchModel = new StopwatchViewModel();
    this.IsValid = false;
    this.isLoaded = false;
  }

  @Mutation setServerDiff(seconds: number) {
    this.serverDiffSeconds = seconds;
  }

  @Action
  async loadServerDiff() {
    this.setServerDiff(await stStopwatchApi.getServerDiff(DateTime.utc().toISO()));
  }

  @Action
  public async loadStopwatch() {
    await this.loadServerDiff();
    let stopwatch = await stStopwatchApi.getRunning();

    if (stopwatch) {
      this.setStopwatchModel(stopwatch);
    } else {
      this.setStopwatchModel(new StopwatchViewModel());
    }
  }

  @Action
  public async startStopwatchForProject(projectId: number) {
    await this.loadServerDiff();

    if (await stStopwatchApi.getRunning()) {
      notifyError('Es läuft bereits eine Stoppuhr');
      await this.loadStopwatch();
    } else {
      await stStopwatchApi.startForProject(projectId);
      this.setStopwatchModel(await stStopwatchApi.getRunning());
    }
  }

  @Action
  public async resumeStopwatch(timeEntryId: number) {
    await this.loadServerDiff();

    await stStopwatchApi.resume(timeEntryId);
    this.setStopwatchModel(await stStopwatchApi.getRunning());
  }

  @Action
  async stopStopwatch() {
    if (!(await stStopwatchApi.getRunning())) {
      notifyError('Es läuft keine Stoppuhr');
      await this.loadStopwatch();
    } else {
      await stStopwatchApi.stop();
      this.setStopwatchModel(new StopwatchViewModel());
    }
  }

  @Action
  async deleteStopwatch() {
    if (!(await stStopwatchApi.getRunning())) {
      notifyError('Es läuft keine Stoppuhr');
      await this.loadStopwatch();
    } else {
      await stStopwatchApi.delete();
      this.setStopwatchModel(new StopwatchViewModel());
    }
  }

  @Action
  public async initializeStore() {
    try {
      await this.loadServerDiff();
      let stopwatch = await stStopwatchApi.getRunning();

      if (stopwatch) {
        this.setStopwatchModel(stopwatch);
      }

      this.setIsLoaded(true);
    } catch {
      this.setIsLoaded(false);
    }
  }
}

export const StopwatchStore = getModule(StopwatchModule);
